import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser.js';
import { organizationService, organizationUserService, profileService } from '@syscolect/domain';
import { rtLogService, useDispatchHookEvent } from '@rammetec/core';
import { useSystemApiWsClient } from '../context/SystemApiWsContext.js';
import React from 'react';
const SessionContext = createContext(undefined);
/**
 * Provider component that holds the single source of truth for the user session.
 */
export const SessionProvider = ({ children }) => {
    const { currentUserId, currentUserData, isLoading: isUserLoading } = useCurrentUser();
    const queryClient = useQueryClient();
    const wsClient = useSystemApiWsClient();
    // -- Organization State --
    const [currentOrganizationId, setCurrentOrganizationId] = useState(null);
    const [isAutoSelectingOrg, setIsAutoSelectingOrg] = useState(false);
    const hasInitializedOrg = useRef(false);
    // -- Profile State --
    const [currentProfileId, setCurrentProfileId] = useState(null);
    const [isAutoSelectingProfile, setIsAutoSelectingProfile] = useState(false);
    const hasInitializedProfile = useRef(false);
    const autoSelectProfileFailedRef = useRef({});
    // Track previous userId to detect user changes (e.g., impersonation)
    const previousUserId = useRef(undefined);
    // Reset profile state when user changes (e.g., impersonation)
    useEffect(() => {
        if (currentUserId && previousUserId.current && previousUserId.current !== currentUserId) {
            rtLogService.info('User changed, resetting profile state', {
                previousUserId: previousUserId.current,
                newUserId: currentUserId
            });
            setCurrentProfileId(null);
            hasInitializedProfile.current = false;
            setIsAutoSelectingProfile(false);
        }
        previousUserId.current = currentUserId;
    }, [currentUserId]);
    // ---------------------------------------------------------------------------
    // Organization Logic
    // ---------------------------------------------------------------------------
    // Sync local Org state with user data ONLY on initial load
    useEffect(() => {
        if (currentUserData?.currentOrg && !hasInitializedOrg.current) {
            setCurrentOrganizationId(currentUserData.currentOrg);
            hasInitializedOrg.current = true;
        }
    }, [currentUserData?.currentOrg]);
    // Query to find a valid organization if none is set
    const { data: validOrganizations } = useQuery({
        queryKey: ['validOrganizations', currentUserId],
        queryFn: () => currentUserId ? organizationService.getValidOrganizations(currentUserId) : Promise.resolve([]),
        enabled: !!currentUserId && !currentOrganizationId && !isUserLoading,
    });
    /**
     * Switch the current organization with optimistic update
     */
    const switchOrganization = useCallback(async (orgId) => {
        if (!currentUserId)
            return;
        // 1. Optimistic Update
        setCurrentOrganizationId(orgId);
        setCurrentProfileId(null); // Reset profile when switching org
        hasInitializedProfile.current = false; // Allow profile to reinitialize for new org
        // Notify system-api about the org change
        wsClient?.send({ kind: 'control', type: 'orgChanged', payload: { orgId } });
        wsClient?.updateMetadata({ orgId });
        // 2. Cache Patch
        queryClient.setQueryData(['user', currentUserId], (oldData) => {
            if (!oldData)
                return oldData;
            return {
                ...oldData,
                currentOrg: orgId
            };
        });
        // 3. Persistence
        try {
            await organizationService.setCurrentOrganization(currentUserId, orgId);
            // Note: We don't invalidate ['user', currentUserId] because:
            // 1. User data doesn't change when switching org
            // 2. currentOrg is already patched in cache above
            // 3. Invalidating causes navigation side effects in AppNavigator
            // Align invalidations with the query keys actually used across hooks/UI
            queryClient.invalidateQueries({ queryKey: ['org', orgId] });
            queryClient.invalidateQueries({ queryKey: ['orgUser', orgId, currentUserId] });
            queryClient.invalidateQueries({ queryKey: ['orgUsers', orgId] });
            queryClient.invalidateQueries({ queryKey: ['userProfiles', orgId, currentUserId] });
            queryClient.invalidateQueries({ queryKey: ['userProfileIds', orgId, currentUserId] });
            queryClient.invalidateQueries({ queryKey: ['availableProfileIds', orgId, currentUserId] });
            queryClient.invalidateQueries({ queryKey: ['validOrganizations', currentUserId] });
        }
        catch (error) {
            rtLogService.error('Error persisting organization switch', { userId: currentUserId, orgId, error });
            // Only invalidate user query on error to restore correct state
            queryClient.invalidateQueries({ queryKey: ['user', currentUserId] });
        }
    }, [currentUserId, queryClient]);
    // Auto-select organization if none is set
    useEffect(() => {
        if (!currentUserId || isUserLoading || currentOrganizationId || !validOrganizations || validOrganizations.length === 0) {
            return;
        }
        const autoSelectOrg = async () => {
            if (isAutoSelectingOrg)
                return;
            const firstOrg = validOrganizations[0];
            rtLogService.info('Auto-selecting first valid organization', { userId: currentUserId, orgId: firstOrg.id });
            setIsAutoSelectingOrg(true);
            try {
                await switchOrganization(firstOrg.id);
            }
            catch (error) {
                rtLogService.error('Error auto-selecting organization', { error });
            }
            finally {
                setIsAutoSelectingOrg(false);
            }
        };
        autoSelectOrg();
    }, [currentUserId, isUserLoading, currentOrganizationId, validOrganizations, isAutoSelectingOrg, switchOrganization]);
    // Sync current org to system-api WS — send on connect/reconnect and when org changes
    useEffect(() => {
        if (!wsClient || !currentOrganizationId)
            return;
        const sendOrgChanged = () => {
            wsClient.send({ kind: 'control', type: 'orgChanged', payload: { orgId: currentOrganizationId } });
            wsClient.updateMetadata({ orgId: currentOrganizationId });
        };
        // Send immediately if already open
        if (wsClient.getStatus() === 'OPEN') {
            sendOrgChanged();
        }
        // Also send when connection (re)opens
        const unsub = wsClient.onStatusChange((status) => {
            if (status === 'OPEN') {
                sendOrgChanged();
            }
        });
        return unsub;
    }, [wsClient, currentOrganizationId]);
    // ---------------------------------------------------------------------------
    // Profile Logic
    // ---------------------------------------------------------------------------
    /**
     * Switch the current profile with optimistic update
     */
    const switchProfile = useCallback(async (profileId) => {
        if (!currentUserId || !currentOrganizationId)
            return false;
        // 1. Optimistic Update
        setCurrentProfileId(profileId);
        // 2. Cache Patch
        queryClient.setQueryData(['orgUser', currentOrganizationId, currentUserId], (oldData) => {
            if (!oldData || !oldData.orgUserData)
                return oldData;
            return {
                ...oldData,
                orgUserData: {
                    ...oldData.orgUserData,
                    currentProfile: profileId
                }
            };
        });
        // 3. Persistence
        try {
            await organizationUserService.setUserCurrentProfile(currentOrganizationId, currentUserId, profileId);
            queryClient.invalidateQueries({ queryKey: ['orgUser', currentOrganizationId, currentUserId] });
            queryClient.invalidateQueries({ queryKey: ['profile', currentOrganizationId, profileId] });
            return true;
        }
        catch (error) {
            rtLogService.error('Error persisting profile switch', { userId: currentUserId, orgId: currentOrganizationId, profileId, error });
            queryClient.invalidateQueries({ queryKey: ['orgUser', currentOrganizationId, currentUserId] });
            return false;
        }
    }, [currentUserId, currentOrganizationId, queryClient]);
    // Fetch OrgUserData to get stored profile preference
    const { data: orgUserData } = useQuery({
        queryKey: ['orgUser', currentOrganizationId, currentUserId],
        queryFn: () => (currentOrganizationId && currentUserId)
            ? organizationService.getOrganizationUser(currentOrganizationId, currentUserId)
            : null,
        enabled: !!currentOrganizationId && !!currentUserId
    });
    // Sync profile ID from stored preference ONLY on initial load or org change
    useEffect(() => {
        if (!currentOrganizationId || !orgUserData?.orgUserData)
            return;
        const storedProfileId = orgUserData.orgUserData.currentProfile;
        // Only initialize if we haven't done so for this org yet
        if (!hasInitializedProfile.current && storedProfileId) {
            setCurrentProfileId(storedProfileId);
            hasInitializedProfile.current = true;
        }
    }, [orgUserData, currentOrganizationId]);
    // Query available profiles for auto-selection
    const { data: userProfileIds } = useQuery({
        queryKey: ['userProfileIds', currentOrganizationId, currentUserId],
        queryFn: () => (currentOrganizationId && currentUserId)
            ? profileService.getUserProfileIds(currentOrganizationId, currentUserId)
            : Promise.resolve([]),
        // Only fetch if no profile is selected AND we've checked if there's a stored profile
        enabled: !!currentOrganizationId && !!currentUserId && !currentProfileId && !!orgUserData && !orgUserData.orgUserData?.currentProfile
    });
    // Auto-select profile if none is set
    useEffect(() => {
        // Only auto-select if there's no stored profile preference
        const hasStoredProfile = orgUserData?.orgUserData?.currentProfile;
        const autoSelectKey = (currentOrganizationId && currentUserId) ? `${currentOrganizationId}:${currentUserId}` : null;
        if (!currentOrganizationId || !currentUserId || currentProfileId || !userProfileIds || userProfileIds.length === 0 || hasStoredProfile) {
            return;
        }
        // Guard against infinite retry loops when persistence fails and queries are invalidated/refetched.
        if (autoSelectKey && autoSelectProfileFailedRef.current[autoSelectKey]) {
            return;
        }
        const autoSelectProfile = async () => {
            if (isAutoSelectingProfile)
                return;
            const firstProfileId = userProfileIds[0];
            rtLogService.info('Auto-selecting first profile', { userId: currentUserId, orgId: currentOrganizationId, profileId: firstProfileId });
            setIsAutoSelectingProfile(true);
            try {
                const ok = await switchProfile(firstProfileId);
                if (ok) {
                    hasInitializedProfile.current = true;
                    if (autoSelectKey) {
                        autoSelectProfileFailedRef.current[autoSelectKey] = false;
                    }
                }
                else {
                    if (autoSelectKey) {
                        autoSelectProfileFailedRef.current[autoSelectKey] = true;
                    }
                }
            }
            catch (error) {
                rtLogService.error('Error auto-selecting profile', { error });
                if (autoSelectKey) {
                    autoSelectProfileFailedRef.current[autoSelectKey] = true;
                }
            }
            finally {
                setIsAutoSelectingProfile(false);
            }
        };
        autoSelectProfile();
    }, [currentOrganizationId, currentUserId, currentProfileId, userProfileIds, isAutoSelectingProfile, switchProfile, orgUserData]);
    useDispatchHookEvent('SessionProvider', {
        currentOrganizationId,
        currentProfileId,
        isAutoSelectingOrg,
        isAutoSelectingProfile,
        hasUser: !!currentUserId
    });
    const sessionStatus = (() => {
        if (isUserLoading)
            return 'resolving';
        if (!currentUserId)
            return 'resolving';
        if (!currentOrganizationId) {
            if (validOrganizations === undefined)
                return 'resolving';
            if (validOrganizations.length === 0)
                return 'no-org';
            // validOrgs present but auto-select effect hasn't fired yet, or is in flight
            return 'resolving';
        }
        // org is set — wait for profile resolution to settle before opening the shell
        if (currentProfileId)
            return 'ready';
        if (orgUserData === undefined)
            return 'resolving'; // orgUserData query still in flight
        if (orgUserData?.orgUserData?.currentProfile)
            return 'resolving'; // stored profile, sync effect pending
        if (isAutoSelectingProfile)
            return 'resolving'; // auto-select in flight
        if (orgUserData !== null) {
            // userProfileIds only loads when orgUserData is present with no stored profile
            if (userProfileIds === undefined)
                return 'resolving'; // profile list query in flight
            if (userProfileIds.length > 0)
                return 'resolving'; // auto-select effect not yet fired
        }
        return 'ready'; // determined: no profiles to select (genuine no-profile state)
    })();
    const value = {
        currentOrganizationId,
        currentProfileId,
        switchOrganization,
        switchProfile,
        isLoading: isUserLoading || isAutoSelectingOrg || isAutoSelectingProfile,
        isAutoSelectingOrg,
        isAutoSelectingProfile,
        sessionStatus,
    };
    return React.createElement(SessionContext.Provider, { value }, children);
};
/**
 * Hook to consume the user's session state.
 * Must be used within a SessionProvider.
 */
export const useSessionManager = () => {
    const context = useContext(SessionContext);
    if (context === undefined) {
        throw new Error('useSessionManager must be used within a SessionProvider');
    }
    return context;
};
//# sourceMappingURL=useSessionManager.js.map